import React from 'react';
import * as Yup from 'yup';
import { Field, FormikConsumer, useField } from 'formik';
import { createUseStyles } from 'react-jss';

import { Theme } from '@app/theme';
import {
  availableLocales,
  ParticipantField,
  PriceTier,
  RecruitmentBy,
  Session,
  StudyType,
  useIsAdmin,
  useIsBeta,
  useParticipantFields,
  usePlan,
  useRecruitment,
  useTargetingPlanOffer,
} from '@app/api';
import {
  CountryKeySelect,
  InformationBox,
  Input,
  LocaleSelect,
  NumberInput,
  OrganizationMemberSelect,
  CheckboxBlockField,
  SubmitButtonsLine,
  Button,
  QuestionTooltip,
  useBoolModalState,
  TextArea,
} from 'components';

import { SessionFormSection } from './SessionFormSection';
import { useTranslation } from 'react-i18next';
import { ChangePriceTierModal } from './ChangePriceTierModal';

interface Props {
  session: Session;
  name: string;
  participantLimits?: { min: number; max: number };
  recruitmentName: string;
  priceTier?: PriceTier;
}

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  checkbox: {
    marginTop: theme.marginBase,
  },
  tierErrorBox: {
    marginTop: theme.marginBase,
    marginBottom: theme.marginBase * 3,
  },
  checkboxContent: {
    flex: 1,
    display: 'flex',
    gap: theme.marginBase * 2,
    justifyContent: 'space-between',
  },
}));
export const getSessionValidationSchema = (
  participantLimits: { min: number; max: number } = {
    min: 1,
    max: Number.MAX_SAFE_INTEGER,
  },
  studyType: StudyType,
) => {
  const shape: { [key in keyof Session]?: Yup.Schema<any> } = {
    locale: Yup.string().required(),
    internalName: Yup.string().nullable(),
    numberOfParticipantRequired: Yup.number()
      .required()
      .min(participantLimits.min)
      .max(participantLimits.max),
    unmoderatedOverflowAcknoledgedAt:
      studyType === StudyType.MODERATED ? undefined : Yup.date().required(),
  };
  return Yup.object().shape(shape);
};

export const getSessionInitialValue = (session: Session) => ({
  internalName: session.internalName,
  numberOfParticipantRequired: session.numberOfParticipantRequired,
  locale: session.locale,
  ownerId: session.ownerId,
  unmoderatedOverflowAcknoledgedAt: session.unmoderatedOverflowAcknoledgedAt,
});

export const StudySettingFields = ({
  session,
  name,
  recruitmentName,
  priceTier,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const isBeta = useIsBeta();
  const { data: participantFields = [] } = useParticipantFields();
  const countryParticipantField = participantFields.find(
    ({ name }) => name === 'countryKey',
  ) as ParticipantField;

  const { data: plan, isLoading: planIsLoading } = usePlan(session.planId);
  const offer = useTargetingPlanOffer(plan && plan.id);
  const [isPriceTierModalOpened, openPriceTierModal, closePriceTierModal] =
    useBoolModalState(false);

  const allLocalesAllowed =
    !offer || !offer.criteria || offer.criteria.includes('locales');

  const { data: recruitment } = useRecruitment(session.recruitmentId);

  const [{ value }] = useField(name);

  return (
    <SessionFormSection title={t('editSession.settings.title')}>
      <Input
        name={`${name}.internalName`}
        label={t('sessions.display.internalName', { context: 'form' })}
        placeholder={t('sessions.placeholder.internalName')}
      />
      <NumberInput
        type="number"
        name={`${name}.numberOfParticipantRequired`}
        label={t('sessions.display.numberOfParticipantRequired')}
        noMargin
      />

      {priceTier &&
        (value.numberOfParticipantRequired >
          (priceTier.maximum || Number.MAX_SAFE_INTEGER) ||
          value.numberOfParticipantRequired < priceTier.minimum) && (
          <InformationBox
            variant="danger"
            title={t('editSession.settings.outOfThier.title', priceTier)}
            className={classes.thierErrorBox}
          >
            {t('editSession.settings.outOfThier.message', {
              min: priceTier.minimum,
              max: priceTier.maximum,
              numberOfParticipants: value.numberOfParticipantRequired,
              context: priceTier.maximum ? '' : 'NO_MAX',
            })}
            <SubmitButtonsLine>
              <Button color="red" onClick={openPriceTierModal} variant="muted">
                {t('editSession.settings.outOfThier.button')}
              </Button>
            </SubmitButtonsLine>
          </InformationBox>
        )}

      {session.studyType !== StudyType.MODERATED && (
        <Field name={`${name}.unmoderatedOverflowAcknoledgedAt`}>
          {({ field: { value }, form: { setFieldValue } }: any) => (
            <CheckboxBlockField
              checked={!!value}
              className={classes.checkbox}
              name={`${name}.unmoderatedOverflowAcknoledgedAt`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(
                  `${name}.unmoderatedOverflowAcknoledgedAt`,
                  e.target.checked ? new Date() : null,
                );
                return false;
              }}
              error={t('editSession.settings.unmoderatedOverflow.required')}
            >
              <div className={classes.checkboxContent}>
                {t('editSession.settings.unmoderatedOverflow.checkbox')}
                <QuestionTooltip
                  title={t('editSession.settings.unmoderatedOverflow.message')}
                />
              </div>
            </CheckboxBlockField>
          )}
        </Field>
      )}

      {recruitment?.recruitmentBy !== RecruitmentBy.CLIENT && (
        <CountryKeySelect
          name={`${recruitmentName}.countryKeys`}
          field={countryParticipantField}
          label={t('sessions.display.countryKey')}
          placeholder={t('editSession.settings.worldwide')}
          opaquePlaceholder
          isMulti
        />
      )}

      <FormikConsumer>
        {({ values }) =>
          !planIsLoading &&
          (offer || !plan) && (
            <LocaleSelect
              name={`${name}.locale`}
              label={t('sessions.display.locale')}
              restricted={!allLocalesAllowed}
              countries={values[recruitmentName].countryKeys}
              written
            />
          )
        }
      </FormikConsumer>

      {(isAdmin || isBeta) && (
        <TextArea
          name={`${recruitmentName}.targetInformation`}
          label={t('editSession.settings.targetInformation.label')}
        />
      )}

      <FormikConsumer>
        {({ values }) =>
          values[name].locale &&
          !availableLocales.includes(values[name].locale) && (
            <InformationBox
              title={t('editSession.public.localeNotReady.title')}
              text={t('editSession.public.localeNotReady.text')}
              variant="danger"
            />
          )
        }
      </FormikConsumer>

      <OrganizationMemberSelect
        name={`${name}.ownerId`}
        selectionType="clientId"
        label={t('sessions.display.owner')}
        organizationId={session.organizationId}
      />

      <ChangePriceTierModal
        isOpen={isPriceTierModalOpened}
        onRequestClose={closePriceTierModal}
        session={session}
      />
    </SessionFormSection>
  );
};
